import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Box, Typography, CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import v2Api from "../../utils/v2Api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const todayStr = () => new Date().toISOString().slice(0, 10);
const datePart = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");

const EditTakerDialog = ({ taker, open, onClose, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [form, setForm] = useState({ email: "", name: "", phone: "", notes: "" });
    const [expiresOn, setExpiresOn] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !taker) return;
        setForm({
            email: taker.email || "", name: taker.name || "",
            phone: taker.phone || "", notes: taker.notes || "",
        });
        setExpiresOn(datePart(taker.expires_at));
    }, [open, taker]);

    const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSave = async () => {
        if (!EMAIL_RE.test(form.email.trim())) {
            enqueueSnackbar("유효한 이메일을 입력하세요.", { variant: "warning" });
            return;
        }
        try {
            setSubmitting(true);
            await v2Api.patch(`/placement/links/${taker.id}`, {
                email: form.email.trim(),
                name: form.name || null,
                phone: form.phone || null,
                notes: form.notes || null,
                expires_on: expiresOn || null,
                clear_expiry: !expiresOn, // blank date = no expiry
            });
            enqueueSnackbar("저장되었습니다.", { variant: "success" });
            onSaved?.();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "저장에 실패했습니다.", { variant: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit placement link</DialogTitle>
            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={2} pt={1}>
                    <Box display="flex" gap={2}>
                        <TextField label="Email" required value={form.email} onChange={setField("email")} fullWidth />
                        <TextField label="Name" value={form.name} onChange={setField("name")} fullWidth />
                        <TextField label="Phone" value={form.phone} onChange={setField("phone")} fullWidth />
                    </Box>
                    <TextField
                        label="Notes (office-only)" value={form.notes} onChange={setField("notes")}
                        multiline minRows={2} fullWidth
                    />
                    <TextField
                        type="date" label="Valid through (end of day)" value={expiresOn}
                        onChange={(e) => setExpiresOn(e.target.value)}
                        InputLabelProps={{ shrink: true }} inputProps={{ min: todayStr() }}
                        sx={{ width: 260 }}
                        helperText="Extend or change the deadline. Blank = no expiry."
                    />
                    <Typography variant="caption" color="text.secondary">
                        Instructions are shared per-test and set when generating links.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained" color="secondary" onClick={handleSave} disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} /> : null}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditTakerDialog;
